import {useState, useEffect} from 'react';

import {useInputMenuContext} from 'context/InputMenu';

import type * as I from 'interfaces';

import addEditCageBackendHandler, {cageInitialState, handleCommunicationSuccess} from 'helpers/addEdit/cage';
import Alert from 'helpers/Alert';
import useParseURL from 'hooks/useParseURL';
import useIsMounted from 'hooks/useIsMounted';
import {type CageResponse} from 'client';
import {useTranslation} from 'react-i18next';
import {useGetCage, useGetCompanies, useGetPlan, useGetPlanFishTypes} from 'hooks/swr';
import useGetLongTermRange from 'hooks/useGetLongTermRange';

function useAddEditCage() {
  const {planId, siteId} = useParseURL();

  const inputMenuContext = useInputMenuContext();
  const inputMenuState = inputMenuContext?.state;
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const cageId = inputMenuState?.formData?.id;
  const formSiteId = inputMenuState?.formData?.siteId;
  const method = inputMenuState?.method;

  const {t} = useTranslation('common');
  const isMounted = useIsMounted();
  const _siteId = formSiteId ?? siteId;
  const {data: plan, isLoading: isPlanLoading} = useGetPlan({resourceId: planId});
  const {data: serverCage, isLoading: isCageLoading} = useGetCage({resourceId: cageId});
  const {data: fishTypes, isLoading: isFishTypesLoading} = useGetPlanFishTypes({resourceId: planId});
  const defaultFishType = fishTypes?.[0];
  const {data: companies} = useGetCompanies({resourceId: planId});
  const defaultCompany = companies?.find((company) => company.isDefault);
  const isLoading = (cageId && (isPlanLoading || isCageLoading)) ?? isFishTypesLoading;

  const [cage, setCage] = useState<I.ICage | undefined>();

  const [createUpdateCage, setCreateUpdateCage] = useState<boolean>(false);
  const {startDate, endDate} = useGetLongTermRange();

  const communicationSuccess = (cage: CageResponse) => {
    startDate &&
      endDate &&
      handleCommunicationSuccess({
        isUpdate: method === 'edit',
        setCage,
        setCreateUpdateCage,
        planId,
        cage,
        inputMenuState,
      });
  };

  useEffect(() => {
    if (plan && defaultFishType && defaultCompany) {
      if (cageId && serverCage) {
        setCage(
          cageInitialState({
            plan,
            defaultFishType,
            defaultCompany,
            selectedCage: serverCage,
            selectedSiteId: serverCage.siteId,
          }),
        );
      } else if (!cageId) {
        setCage(
          cageInitialState({
            plan,
            defaultFishType,
            defaultCompany,
            selectedCage: serverCage,
            selectedSiteId: _siteId ?? undefined,
          }),
        );
      }
    }
  }, [serverCage, plan, cageId, _siteId, defaultFishType, defaultCompany]);

  useEffect(() => {
    if (method && cage) {
      void addEditCageBackendHandler({
        operationAddEdit: method,
        cage,
        createUpdateCage,
        setCreateUpdateCage,
        inputMenuDispatch,
        communicationSuccess,
        isMounted,
        t,
      });
    }
    if (cage && cage.siteId === undefined && createUpdateCage) {
      Alert.warning(t('noSiteError'));
      setCreateUpdateCage(false);
    }
  }, [createUpdateCage, cage, method]);

  return {
    cage,
    setCage,
    setCreateUpdateCage,
    isLoading,
  };
}

export default useAddEditCage;
